<template>
  <div>
    <div id="loginback">
      <div id="login">

        <!-- Step 1: Login form -->
        <form v-if="!showEmailCodeStep" @submit.prevent="login">
          <input id="username" name="username" type="text" size="40" class="login_name" v-model="username"
            aria-required="true" aria-invalid="false" placeholder="Username" required>
          <input id="password" name="password" type="password" size="40" class="login_password" v-model="password"
            placeholder="Password" required>

          <!-- Verification method selector -->
          <div class="verify-method-label">Receive verification code via:</div>
          <div class="verify-method-row">
            <label class="method-option" :class="{ active: verifyMethod === 'email' }">
              <input type="radio" v-model="verifyMethod" value="email" class="method-radio" />
              <span class="method-icon">✉️</span>
              <span class="method-text">Email</span>
            </label>
            <label class="method-option" :class="{ active: verifyMethod === 'call' }">
              <input type="radio" v-model="verifyMethod" value="call" class="method-radio" />
              <span class="method-icon">📞</span>
              <span class="method-text">Phone Call</span>
            </label>
          </div>

          <input type="submit" value="Sign In" class="login_submit"><br>
        </form>

        <!-- Step 2: Verification code -->
        <form v-else @submit.prevent="verifyCode">
          <div class="step-title">Two-Factor Authentication</div>
          <div class="step-description">
            <span v-if="verifyMethod === 'email'">A verification code has been sent to your email address.</span>
            <span v-else>You will receive a phone call with your verification code.</span>
            <br />Please enter the code below to continue.
            <div v-if="sentTo" class="sent-to">
              <span v-if="verifyMethod === 'email'">Sent to: </span>
              <span v-else>Calling: </span>
              <strong dir="ltr">{{ sentTo }}</strong>
            </div>
          </div>
          <div v-if="verifyMethod === 'call'" class="call-hint">
            <v-icon small class="call-hint-icon">mdi-information-outline</v-icon>
            Enter the <strong>last 6 digits</strong> of the phone number that called you
          </div>

          <input id="verifyCode" name="verifyCode" type="text" size="25" class="login_name login_code"
            v-model="verificationCode" placeholder="6-digit verification code" maxlength="6"
            autocomplete="one-time-code" required>

          <!-- Remember IP checkbox row -->
          <div class="remember-row">
            <div class="tooltip-wrapper">
              <span class="info-icon">?</span>
              <div class="tooltip-box">
                Your IP address will be saved to the approved list for 30 days.
                During this period you can log in without two-factor authentication from the same network.
              </div>
            </div>
            <div class="remember-right">
              <label class="remember-label">
                <span class="remember-text">Remember me for 30 days</span>
                <input type="checkbox" v-model="rememberIp" class="remember-checkbox" />
              </label>
            </div>
          </div>

          <input type="submit" value="Verify Code" class="login_submit"><br>
          <div style="text-align: center; margin-top: 8px;">
            <span @click="backToLogin" class="Back" style="font-size: 0.95em; opacity: 0.8;">← Back to Login</span>
          </div>
        </form>

        <div v-if="!showEmailCodeStep" style="display: flex; justify-content: space-between; width: 95%;">
          <div><span @click="forgotPassword" class="Back">Forgot Password?</span></div>
          <div><span @click="openRegister" class="Back">Create Account</span></div>
        </div>
        <input id="submitme" name="submitme" value="1" type="hidden">

      </div>
    </div>

    <div class="centered-text">
      <div id="foter">
        <span style="color: white; font-weight: bold; font-size: 25px; margin-left: 60px;">Management System</span>
        <span style="color: #ffb91a; font-weight: bold; font-size: 25px; margin-left: 40px; display: block;">Whatsapp
          Phone</span>
      </div>
    </div>

    <template>
      <v-overlay :value="overlay">
        <v-progress-circular indeterminate size="84" color="#0d2c6d"></v-progress-circular>
      </v-overlay>
    </template>

    <SnackBar v-model="snackbar" :snacbarText="snacbarText" :snackbarColorBt="snackbarColorBt"></SnackBar>
  </div>
</template>

<script>
import ApiServices from '@/services/api.service'
import SnackBar from '@/components/widgets/snackBar.vue'
import { mapMutations } from 'vuex';

export default {

  components: { SnackBar },

  data: () => ({
    username: "",
    password: "",
    verificationCode: "",
    verifyMethod: "email",   // "email" | "call"
    rememberIp: false,
    showEmailCodeStep: false,
    sentTo: "",
    snackbar: false,
    snackbarColorBt: "green",
    snacbarText: "",
    overlay: false,
  }),

  methods: {
    ...mapMutations(['TOGGLE_USER_DETAILS']),

    async login() {
      const lineNumber = this.username;
      const password = this.password;
      const verifyMethod = this.verifyMethod;

      this.overlay = true;
      let MyJSON = JSON.stringify({ lineNumber, password, verifyMethod });
      let api = process.env.VUE_APP_BASE_URL + "/auth/login";

      try {
        const response = await fetch(api, ApiServices.requestOptions("POST", MyJSON, ""));
        this.overlay = false;
        const jsonObject = await response.json();

        if (response.status >= 400) {
          this.showSnackBar("Login error: " + jsonObject.message, "red");
        } else if (response.status === 200) {
          if (jsonObject.token) {
            // IP is whitelisted — direct login
            this.showSnackBar("Login successful!", "green");
            localStorage.setItem('token', jsonObject.token);
            this.TOGGLE_USER_DETAILS();
            this.$store.commit('SET_TIMEZONE', null);
            this.$router.replace({ name: "home" });
          } else {
            // 2FA required
            this.showEmailCodeStep = true;
            this.verificationCode = "";
            this.rememberIp = false;
            this.sentTo = jsonObject.sentTo || "";
            const msg = verifyMethod === 'email'
              ? "Verification code sent to your email"
              : "You will receive a phone call shortly";
            this.showSnackBar(jsonObject.message || msg, "green");
          }
        } else {
          this.showSnackBar("Server error", "red");
        }
      } catch (error) {
        this.overlay = false;
        this.showSnackBar("Error Login: " + error, "red");
      }
    },

    async verifyCode() {
      const lineNumber = this.username;
      const verificationCode = this.verificationCode;
      const rememberIp = this.rememberIp;

      this.overlay = true;
      let MyJSON = JSON.stringify({ lineNumber, verificationCode, rememberIp });
      let api = process.env.VUE_APP_BASE_URL + "/auth/verifyCode";

      try {
        const response = await fetch(api, ApiServices.requestOptions("POST", MyJSON, ""));
        this.overlay = false;
        const jsonObject = await response.json();

        if (response.status >= 400) {
          this.showSnackBar("Invalid code: " + jsonObject.message, "red");
        } else if (response.status === 200) {
          this.showSnackBar("Login successful!", "green");
          localStorage.setItem('token', jsonObject.token);
          this.TOGGLE_USER_DETAILS();
          this.$store.commit('SET_TIMEZONE', null);
          this.$router.replace({ name: "home" });
        } else {
          this.showSnackBar("Server error", "red");
        }
      } catch (error) {
        this.overlay = false;
        this.showSnackBar("Error: " + error, "red");
      }
    },

    backToLogin() {
      this.showEmailCodeStep = false;
      this.verificationCode = "";
      this.rememberIp = false;
    },

    async forgotPassword() {
      const lineNumber = this.username;
      if (!lineNumber) return this.showSnackBar("Please enter your username", "red");

      this.overlay = true;
      let MyJSON = JSON.stringify({ lineNumber });
      let api = process.env.VUE_APP_BASE_URL + "/auth/forgotPassword";

      try {
        const response = await fetch(api, ApiServices.requestOptions("POST", MyJSON, ""));
        this.overlay = false;
        const jsonObject = await response.json();
        if (response.status >= 400) {
          this.showSnackBar("Error: " + jsonObject.message, "red");
        } else if (response.status === 200) {
          this.showSnackBar("A password reset email has been sent to you", "green");
        } else {
          this.showSnackBar("Server error", "red");
        }
      } catch (error) {
        this.overlay = false;
        this.showSnackBar("Error: " + error, "red");
      }
    },

    openRegister() { this.$emit("openRegister"); },

    showSnackBar(message, color) {
      this.snacbarText = message;
      this.snackbarColorBt = color;
      this.snackbar = true;
    },

    handleResize() { this.isResponsive = window.innerWidth <= 500; },

    handleEnterKeyOtp(event) {
      if (event.keyCode === 13) {
        this.showEmailCodeStep ? this.verifyCode() : this.login();
      }
    },
  },

  watch: {
    $route() { document.title = 'Login | CLICK-CALL'; },
  },

  mounted() {
    document.title = 'Login | CLICK-CALL';
    if (window.innerWidth <= 500) this.isResponsive = true;
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('keydown', this.handleEnterKeyOtp);
    setTimeout(() => {
      const badge = document.querySelector('.grecaptcha-badge');
      if (badge) badge.style.visibility = 'visible';
    }, 500);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('keydown', this.handleEnterKeyOtp);
    const badge = document.querySelector('.grecaptcha-badge');
    if (badge) badge.style.visibility = 'hidden';
  },
}
</script>

<style scoped>
#login {
  width: 65%;
  margin: 4% auto;
  min-height: 40%;
}

div#loginback {
  background: linear-gradient(135deg, #0e3280 0%, #0a2357 100%) !important;
  min-height: 55%;
  position: relative;
  z-index: 99;
}

.login_name {
  font-family: 'Roboto', arial !important;
  border: 0 none !important;
  text-align: left !important;
  padding: 3% 5% 3% 13% !important;
  font-size: 1.25em !important;
  width: 95% !important;
  margin-bottom: 3% !important;
  background-repeat: no-repeat !important;
  background-size: contain !important;
  background-position: 0% -.9vh !important;
  border-radius: .4vw !important;
  background-color: white;
  background-image: url(@/assets/login/icon_u.png);
}

.login_code {
  text-align: center !important;
  letter-spacing: 6px !important;
  font-size: 1.5em !important;
  font-weight: 700 !important;
  background-image: none !important;
  padding: 3% 5% !important;
  margin-bottom: 3% !important;
}

.login_password {
  font-family: 'Roboto', arial;
  border: 0 none;
  text-align: left;
  padding: 3% 5% 3% 13%;
  width: 95%;
  font-size: 1.25em;
  margin-bottom: 4%;
  background-repeat: no-repeat;
  background-size: contain;
  background-position: 0% -.9vh;
  border-radius: .4vw;
  background-color: white;
  background-image: url(@/assets/login/icon_s.png);
}

.login_submit {
  font-family: 'Roboto', arial;
  padding: 2%;
  background: #fdb721;
  width: 95%;
  font-size: 1.57em;
  margin-bottom: 2%;
  cursor: pointer;
  border: none;
  border-radius: .4vw;
  font-weight: 600;
}

.login_submit:hover {
  background: #ffa500;
  transition: background 0.3s ease;
}

.Back {
  text-align: left;
  direction: ltr;
  color: #fff;
  font-size: 1.11em;
  font-family: 'Roboto', arial;
  margin: 1% 0%;
  cursor: pointer;
}

.Back:hover {
  color: #ffb91a;
  transition: color 0.2s ease;
}

/* ===== Verify method selector ===== */
.verify-method-label {
  font-family: 'Roboto', arial;
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.9em;
  text-align: left;
  direction: ltr;
  margin-bottom: 8px;
  width: 95%;
}

.verify-method-row {
  display: flex;
  gap: 10px;
  width: 95%;
  margin-bottom: 5%;
  direction: ltr;
}

.method-option {
  display: flex;
  align-items: center;
  gap: 7px;
  flex: 1;
  cursor: pointer;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1.5px solid rgba(255, 255, 255, 0.25);
  border-radius: .4vw;
  padding: 8px 12px;
  transition: background-color 0.2s, border-color 0.2s;
  direction: ltr;
}

.method-option:hover {
  background-color: rgba(255, 255, 255, 0.18);
}

.method-option.active {
  background-color: rgba(253, 183, 33, 0.18);
  border-color: #ffb91a;
}

.method-radio {
  display: none;
}

.method-icon {
  font-size: 1.15em;
  flex-shrink: 0;
}

.method-text {
  font-family: 'Roboto', arial;
  font-size: 0.95em;
  color: #fff;
  user-select: none;
}

.method-option.active .method-text {
  color: #ffb91a;
  font-weight: 700;
}

/* ===== Remember IP row ===== */
.remember-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 95%;
  margin-bottom: 5%;
  direction: ltr;
}

.remember-right {
  display: flex;
  align-items: center;
}

.remember-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.remember-checkbox {
  width: 17px;
  height: 17px;
  accent-color: #ffb91a;
  cursor: pointer;
  flex-shrink: 0;
}

.remember-text {
  font-family: 'Roboto', arial;
  font-size: 1em;
  color: #fff;
  user-select: none;
}

/* ===== Tooltip ===== */
.tooltip-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.info-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.2);
  color: #fff;
  font-size: 0.8em;
  font-weight: 700;
  font-family: 'Roboto', arial;
  cursor: default;
  border: 1.5px solid rgba(255, 255, 255, 0.45);
  transition: background-color 0.2s, border-color 0.2s;
  user-select: none;
}

.info-icon:hover {
  background-color: rgba(255, 185, 26, 0.45);
  border-color: #ffb91a;
}

.tooltip-box {
  display: none;
  position: absolute;
  left: 28px;
  top: 50%;
  transform: translateY(-50%);
  background-color: #0a2357;
  color: #fff;
  font-family: 'Roboto', arial;
  font-size: 0.82em;
  line-height: 1.6;
  direction: ltr;
  text-align: left;
  padding: 10px 14px;
  border-radius: 8px;
  width: 230px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 185, 26, 0.3);
  z-index: 100;
  pointer-events: none;
}

.tooltip-box::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 100%;
  transform: translateY(-50%);
  border: 6px solid transparent;
  border-right-color: #0a2357;
}

.tooltip-wrapper:hover .tooltip-box {
  display: block;
}

/* ===== Step UI ===== */
.step-title {
  font-family: 'Roboto', arial;
  color: #ffffff;
  font-size: 1.5em;
  font-weight: 700;
  text-align: left;
  margin-bottom: 6px;
}

.step-description {
  font-family: 'Roboto', arial;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.95em;
  text-align: left;
  margin-bottom: 16px;
  line-height: 1.6;
}

.sent-to {
  margin-top: 6px;
  font-size: 0.92em;
  color: rgba(255, 255, 255, 0.65);
  direction: ltr;
  text-align: left;
}

.sent-to strong {
  color: #ffb91a;
  font-family: 'Courier New', monospace;
  letter-spacing: 1px;
}

.call-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  direction: ltr;
  background-color: rgba(255, 185, 26, 0.15);
  border: 1px solid rgba(255, 185, 26, 0.4);
  border-radius: .4vw;
  padding: 9px 12px;
  margin-bottom: 14px;
  font-family: 'Roboto', arial;
  font-size: 0.88em;
  color: #fff;
  line-height: 1.5;
  width: 88%;
}

.call-hint-icon {
  color: #ffb91a !important;
  flex-shrink: 0;
}

/* ===== Footer ===== */
#foter {
  background-image: url("@/assets/footer.svg");
  background-size: 108% auto;
  background-repeat: no-repeat;
  width: 34%;
  height: 16vh;
  position: absolute;
  bottom: 2%;
  z-index: 0;
}

.centered-text {
  text-align: center;
}

/* ===== Responsive ===== */
@media only screen and (max-width: 998px) and (min-height: 600px) {
  #login {
    width: 85%;
  }

  #foter {
    width: 100%;
    position: absolute;
    bottom: 5%;
  }

  .tooltip-box {
    left: auto;
    right: 28px;
  }

  .tooltip-box::after {
    right: auto;
    left: 100%;
    border-right-color: transparent;
    border-left-color: #0a2357;
  }

  .method-option {
    border-radius: 6px;
  }
}

.notification {
  display: block;
  padding: 3% 5% 3% 13%;
  width: 76%;
  border-radius: .4vw;
  margin-bottom: 2vh;
}

@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
</style>